import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "@material-ui/styles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Alert from "@material-ui/lab/Alert";

import ImageEye from "@material-ui/icons/RemoveRedEye";
import ActionPrint from "@material-ui/icons/Print";
import ActionDelete from "@material-ui/icons/Delete";
import CircularProgress from "@material-ui/core/CircularProgress";
import ActionRun from "@material-ui/icons/PlayCircleFilled";

import {
  PageHeader,
  PageFooter,
  WithReduxForm,
  SelectInput,
  NumberInput,
  SimpleShowLayout,
  FunctionField,
  NumberField,
  required,
  Button,
  showNotification,
  translate
} from "jazasoft";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";

import MuiTable from "../../components/MuiTable";
import Table from "../../components/Table";
import CardHeader from "../../components/CardHeader";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useUnit } from "../../hooks";
import SettingKeys from "../../utils/SettingKeys";
import { Entity, OState } from "../../utils/types";
import { listFromCsv, getDistinctValues, isEmpty, inputOptions, toFixed, toFixedValue } from "../../utils/helpers";
import { getCutOrderFlowInfoList, getOrderFlowList } from "../../helpers/orderHelper";
import { LayType, MULTI_PART_EXACT_SAME_BUNDLE, MULTI_PART_MANUAL_PLAN, PART_SHELL, rollPlaneType } from "../../constants";
import hasPrivilege from "../../utils/hasPrivilege";

import { onPrintLay, printLayslip } from "./rollPlanHelper";
import LayDialog from "./LayDialog";
import useTenant from "../../hooks/useTenant";
import { getCutOrderRows } from "../cutplan/cutPlanHelper";
import { ResourceId, Scope } from "../../utils/privilege";
import SplitButton from "../../components/SplitButton";

const MyButton = ({ label, icon, color = "primary", saving, ...rest }) => (
  <Button style={{ margin: "0 1em" }} label={label} variant="contained" color={saving ? "default" : color} {...rest}>
    {saving ? <CircularProgress size={25} thickness={2} color="secondary" /> : icon}
  </Button>
);

const getLayData = orderFlow => {
  if (!orderFlow || !orderFlow.orderConfig || !orderFlow.orderConfig.layType) return { layMap: null, layTabs: null };
  let layMap = JSON.parse(orderFlow.orderConfig.laySlip);
  const { "Remnant Lay": RemnantLay, ...restLays } = layMap;
  let layTabs = [...Object.keys(restLays)];
  layTabs = layTabs.sort((a, b) => layMap[a][0]?.layNo - layMap[b][0]?.layNo);

  if (!isEmpty(RemnantLay) && !orderFlow.reGenerated) {
    layTabs.push("Remnant Lay");
  }
  if (orderFlow.orderConfig.reGeneratedLaySlip) {
    const RemLay = JSON.parse(orderFlow.orderConfig.reGeneratedLaySlip);
    layMap["Remnant Lay"] = RemLay;
    if (!layTabs["Remnant Lay"]) {
      layTabs.push("Remnant Lay");
    }
  }
  return { layMap, layTabs };
};

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  },
  margin: {
    margin: "1.5em"
  },
  form: {
    padding: "1.5em",
    paddingTop: 0
  },
  settings: {
    margin: "1em 1em 0 1em"
  }
});

const RollPlanView = props => {
  const classes = useStyles();
  const { id, roles, hasAccess, translate } = props;

  const [version, setVersion] = React.useState(0);
  const [selectedKey, setSelectedKey] = React.useState(null);
  const [layTabIdx, setLayTabIdx] = useSafeState(0);
  const [dialogState, setDialogState] = useSafeState({ open: false, lay: null });

  const dispatch = useDispatch();
  const [mutate, { loading: busy }] = useMutation();
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);
  const rollFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ROLLS);
  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const messages = useSelector(state => state?.i18n?.messages || {});
  const tenant = useTenant();
  const { allUnitList } = useUnit();

  const { data: order = {} } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" }, version });

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order?.flowInfoList) || isEmpty(order?.orderFlowList)) return [];
    let rows = getCutOrderRows(order, productParts);
    rows = rows.map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
    return rows;
  }, [order, productParts]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty" , render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty" , render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      // { dataKey: "extra", title: "Extra (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "state", title: "Status", filterOptions: { filter: true }, length: 100 }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    }
    return cols;
  }, [flowInfoRows, allUnitList]);

  useDeepEffect(() => {
    if (!isEmpty(flowInfoRows)) {
      setSelectedKey(flowInfoRows[0].key);
    }
  }, [flowInfoRows.length]);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setSelectedKey(row.key);
    }
  };

  const handleClick = (handleSubmit, action) => {
    if (action === "delete") {
      const options = { url: `orders/${id}/lays`, method: "delete", params: { flowId: cutOrder.id } };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Lay slip deleted successfully."));
          setVersion(v => v + 1);
        }
      });
    } else {
      handleSubmit(values => {
        const options = { url: `orders/${id}/lays`, method: "post", data: values, params: { flowId: cutOrder.id, action } };
        mutate(options, {
          onSuccess: () => {
            dispatch(showNotification("Lay slip generated successfully."));
            setVersion(v => v + 1);
          }
        });
      })();
    }
  };

  const defaultExpanded = flowInfoRows.filter(fi => isEmpty(fi.parentKey)).length === 1;

  const cutOrder = flowInfoRows.find(fi => fi.key === selectedKey)?.cutOrder || {};

  const colorList = cutOrder ? listFromCsv(cutOrder.colors) : [];
  const orderConfig = cutOrder ? cutOrder.orderConfig : {};

  const hasSizeGroup = useDeepMemo(() => {
    if (isEmpty(cutOrder?.flowInfoIds) || isEmpty(order?.flowInfoList)) return false;

    const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

    const sbMap = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sizeBreakupList = Object.values(sbMap);
    const hasSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;
    return hasSizeGroup;
  }, [order, cutOrder]);

  const layTypeChoices = useDeepMemo(() => {
    let layTypeChoices = [
      { id: LayType.ALL_MIX, name: translate("resources.rollPlan.fields.setting.layType.all_mix", { _: LayType.ALL_MIX }) },
      { id: LayType.SHADE_WISE, name: translate("resources.rollPlan.fields.setting.layType.shade_wise", { _: LayType.SHADE_WISE }) }
    ];
    if (colorList.length > 1) {
      layTypeChoices.push({
        id: LayType.COLOR_WISE,
        name: translate("resources.rollPlan.fields.setting.layType.color_wise", { _: LayType.COLOR_WISE })
      });
    }
    if (hasSizeGroup) {
      layTypeChoices.push({
        id: LayType.SIZE_GROUP_WISE,
        name: translate("resources.rollPlan.fields.setting.layType.size_group_wise", { _: LayType.SIZE_GROUP_WISE })
      });
    }

    return layTypeChoices;
  }, [colorList, hasSizeGroup]);
  const layTypeMap = layTypeChoices.reduce((acc, e) => ({ ...acc, [e.id]: e.name }), {});

  const cutSetting = !isEmpty(orderConfig?.cutSetting) ? JSON.parse(orderConfig?.cutSetting) : {};

  let initialValues = {
    id: cutOrder?.id,
    layType: cutSetting.layType || orderConfig?.layType || LayType.ALL_MIX,
    recLayHeight: orderConfig?.recLayHeight || null,
    layHeightTolerance: orderConfig?.layHeightTolerance || null
  };
  if (orderConfig?.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE) {
    const shellOrderFlow = getOrderFlowList(order, cutOrder).find(flow => flow.part === PART_SHELL); // TODO: Review
    const shellOrderConfig = shellOrderFlow ? shellOrderFlow.orderConfig : {};
    initialValues = {
      id: cutOrder?.id,
      layType: shellOrderConfig.layType || LayType.ALL_MIX,
      recLayHeight: shellOrderConfig.recLayHeight || null,
      layHeightTolerance: shellOrderConfig.layHeightTolerance || null
    };
  }

  const { layMap, layTabs } = useDeepMemo(() => {
    if (isEmpty(cutOrder)) return { layMap: null, layTabs: [] };
    return getLayData(cutOrder);
  }, [cutOrder]);

  const onLayPrint = ({ orderFlow, lay }) => {
    if (!isEmpty(order) && !isEmpty(orderFlow) && !isEmpty(lay)) {
      onPrintLay({
        order,
        orderFlow,
        lay,
        tenant,
        flowInfoFields,
        orderFields,
        rollFields,
        i18nMessages: messages,
        unit: allUnitList.find(e => e.unitId === orderFlow.unitId) || {}
      });
    }
  };

  const rows = useDeepMemo(() => {
    if (isEmpty(cutOrder) || isEmpty(layTabs) || isEmpty(layMap)) return [];

    const layTab = layTabs[layTabIdx];

    let layList = layMap[layTab];
    let rows = [];
    if (layList && layList.length > 0) {
      layList = layList.sort((a, b) => a.layNo - b.layNo);

      let markerSet = new Set();
      let markerNo = 1;
      rows = layList.map((lay, idx) => {
        const { marker, layNo, rowList } = lay;
        const plies = rowList?.reduce((acc, row) => acc + (row.plannedPly || 0), 0);
        const fabricQty = rowList && rowList.reduce((acc, row) => acc + marker.layLength * row.plannedPly, 0);
        let serialNo;
        if (!markerSet.has(marker.id)) {
          serialNo = markerNo++;
          markerSet.add(marker.id);
        }
        const sizeGroup = getDistinctValues(marker.name?.split(", ").map(e => e.split("_")[0]));
        const markerName = marker.name
          ?.split(", ")
          .map(e => e.split("_")[1])
          .join(", ");

        return {
          serialNo,
          sizeGroup,
          marker: markerName,
          layLength: `${marker.layLength} m`,
          way: marker.way,
          layNo,
          layCount: layList.filter(lay => marker.id === lay.marker.id).length,
          plies: toFixedValue(plies, 0),
          rollCount: rowList && rowList.length,
          fabricQty: toFixed(fabricQty, 1),
          action: [
            <ImageEye key="view" style={{ marginRight: 8, cursor: "pointer" }} onClick={() => setDialogState({ open: true, lay })} />,
            <ActionPrint key="print" style={{ cursor: "pointer" }} onClick={() => onLayPrint({ orderFlow: cutOrder, lay })} />
          ]
        };
      });
    }

    return rows;
  }, [cutOrder, layMap, layTabs, layTabIdx]);

  const columns = useDeepMemo(() => {
    if (isEmpty(cutOrder)) return [];

    const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

    const sizeBreakupList = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []);
    const haveSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup))?.length;

    let columnList = [
      { dataKey: "sizeGroup", title: "Size Group", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "marker", title: "Marker", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "layLength", title: "Lay Length", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "way", title: "Way", align: "center", rowSpan: ({ record }) => (record.serialNo ? record.layCount : 0) },
      { dataKey: "layNo", title: "Lay No", align: "center" },
      { dataKey: "plies", title: "Plies", align: "center" },
      { dataKey: "rollCount", title: "No. Of Rolls", align: "center" }
    ];
    if (cutOrder && cutOrder.orderConfig && cutOrder.orderConfig.layType) {
      columnList.push({ dataKey: "action", title: "Action", align: "center" });
    }

    if (!haveSizeGroup) {
      columnList = columnList.filter(c => c.dataKey !== "sizeGroup");
    }
    return columnList;
  }, [cutOrder]);

  const onPrint = (fullLayslip = false, type = "standard") => {
    if (isEmpty(cutOrder) || isEmpty(layTabs)) return;

    const layTab = layTabs[layTabIdx];
    const options = { url: `rolls`, method: "get", params: { search: `cutOrderFlow.id==${cutOrder.id};parentId=='NULL'`, size: 100000 } };
    mutate(options, {
      onSuccess: rollList => {
        printLayslip({
          order,
          orderFlow: cutOrder,
          layTab: fullLayslip ? null : layTab,
          rollList,
          messages,
          flowInfoFields,
          i18nMessages: messages,
          orderFields,
          rollFields,
          tenant,
          unit: allUnitList.find(e => e.unitId === cutOrder?.unitId) || {},
          type
        });
      }
    });
  };

  return (
    <div>
      <PageHeader title="Roll Plan" />

      <LayDialog
        open={dialogState?.open || false}
        lay={dialogState?.lay}
        orderFlow={cutOrder}
        rollFields={rollFields}
        onClose={() => setDialogState({ open: false, lay: null })}
      />

      <div className={classes.content}>
        <MuiTable
          key={version}
          title={translate("resources.rollPlan.title.cut_order", { _: "Cut Order" })}
          toolbar={true}
          searchable={true}
          pageable={true}
          searchKeys={["style", "color", "flowRef", "customerFlowRef", "fpo"]}
          idKey="key"
          parentIdKey="parentKey"
          columns={flowInfoColumns}
          rows={flowInfoRows}
          defaultExpanded={defaultExpanded}
          expandedColor={["#BFDDF8", "#7AB9F0"]}
          onRowClick={onRowClick}
          rowStyle={({ row }) => ({ backgroundColor: selectedKey === row.key ? "#BFDDF8" : "none" })}
          i18nKey="resources.grouping.fields.flowInfo"
        />

        {(!cutOrder || cutOrder.stateValue < OState.MAIN_CUTPLAN_COMPLETED.stateValue) && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Cut Plan not completed yet.</Typography>
          </Alert>
        )}

        {cutOrder?.orderConfig?.multiPartBundling === MULTI_PART_MANUAL_PLAN && (
          <Alert variant="filled" severity="warning" className={classes.mrTop}>
            <Typography variant="h6">Roll Plan not applicable for Manual Plan.</Typography>
          </Alert>
        )}

        {cutOrder.stateValue >= OState.MAIN_CUTPLAN_COMPLETED.stateValue && cutOrder?.orderConfig?.multiPartBundling !== MULTI_PART_MANUAL_PLAN && (
          <WithReduxForm initialValues={initialValues}>
            {({ handleSubmit, ...rest }) => (
              <div>
                {/* RollPlan Settings */}
                <Card className={classes.mrTop}>
                  <CardHeader title="Roll Plan Settings" />
                  <Divider />
                  <div>
                    {!orderConfig.layType && (
                      <SimpleForm component="div" footer={false} className={classes.form}>
                        <SelectInput
                          source="layType"
                          validate={[required()]}
                          choices={layTypeChoices}
                          disabled={cutSetting?.layType === LayType.SIZE_GROUP_WISE || orderConfig.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE}
                          {...inputOptions(4)}
                        />
                        <NumberInput
                          label="Recommended Lay Plies"
                          source="recLayHeight"
                          validate={[required()]}
                          disabled={orderConfig.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE}
                          {...inputOptions(4)}
                        />
                        <NumberInput
                          label="Lay Ply Tolerance"
                          source="layHeightTolerance"
                          validate={[required()]}
                          disabled={orderConfig.multiPartBundling === MULTI_PART_EXACT_SAME_BUNDLE}
                          {...inputOptions(4)}
                        />
                      </SimpleForm>
                    )}
                    {cutOrder && orderConfig.layType && (
                      <SimpleShowLayout className={classes.settings} record={{ ...cutOrder, ...orderConfig }} footer={false}>
                        <FunctionField
                          source="layType"
                          label="Lay Type"
                          render={record => (isEmpty(record?.layType) ? "" : layTypeMap[record.layType])}
                          xs={12}
                          sm={3}
                        />
                        <NumberField label="Recommended Lay Plies" source="recLayHeight" xs={12} sm={4} />
                        <NumberField label="Lay Ply Tolerance" source="layHeightTolerance" xs={12} sm={4} />
                      </SimpleShowLayout>
                    )}
                  </div>
                </Card>

                {layMap && (
                  <Card className={classes.mrTop}>
                    <Tabs value={layTabIdx} onChange={(e, idx) => setLayTabIdx(idx)} variant="scrollable" scrollButtons="auto">
                      {layTabs.map((tab, idx) => (
                        <Tab key={idx} label={tab === "All Mix" ? "Main Lay" : tab} />
                      ))}
                    </Tabs>

                    <div>
                      <Table classes={{ table: classes.table }} columns={columns} rows={rows} i18nKey="resources.rollPlan.fields.layslip" />
                      {orderConfig.layType && (
                        <div style={{ display: "flex", justifyContent: "flex-end", padding: "1em" }}>
                          <SplitButton
                            primaryIcon={<ActionPrint />}
                            primaryLabel="Print"
                            options={Object.values(rollPlaneType)}
                            style={{ marginRight: "1em" }}
                            onClick={({ type }) => onPrint(false, type)}
                          />
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                <PageFooter className={classes.displayCenter}>
                  {layMap && cutOrder && cutOrder?.orderConfig?.layType === LayType.ALL_MIX && (
                    <SplitButton
                      primaryIcon={<ActionPrint />}
                      primaryLabel="Print"
                      options={Object.values(rollPlaneType)}
                      style={{ marginRight: "1em" }}
                      onClick={({ type }) => onPrint(true, type)}
                    />
                  )}

                  {isEmpty(orderConfig.layType) && hasPrivilege(roles, hasAccess, ResourceId.LAY, Scope.WRITE) && (
                    <Button
                      label="Generate Roll Plan"
                      color="primary"
                      variant="contained"
                      onClick={() => handleClick(handleSubmit, "default")}
                      saving={busy}
                      disabled={busy}>
                      <ActionRun />
                    </Button>
                  )}

                  {cutOrder &&
                    cutOrder.stateValue === OState.ROLL_PLAN_GENERATED.stateValue &&
                    layMap &&
                    hasPrivilege(roles, hasAccess, ResourceId.LAY, Scope.DELETE) && (
                      <MyButton
                        label="Delete"
                        icon={<ActionDelete />}
                        onClick={() => handleClick(handleSubmit, "delete")}
                        saving={busy}
                        disabled={busy}
                      />
                    )}
                </PageFooter>
              </div>
            )}
          </WithReduxForm>
        )}
      </div>
    </div>
  );
};

export default translate(RollPlanView);
